import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Plus, Loader2, MessageCircle, CheckSquare, Cloud, AlignLeft, Star, Brain, ListOrdered, Disc3,
    ArrowUpDown, LayoutGrid, SlidersHorizontal, ImageIcon, Hash, MousePointerClick, CircleDollarSign,
    Smile, Shuffle, TrendingUp, Clock, Target, MessageSquare, Zap, Pencil, Sparkles, AlertCircle, X, Ticket, Cherry
} from 'lucide-react';
import ActivityTypeModal from '../components/ActivityTypeModal';
import EventHeader from '../components/event/EventHeader';
import PollsSidebar from '../components/event/PollsSidebar';
import QrModal from '../components/event/QrModal';
import { useEventPage } from '../hooks/useEventPage';
import { useAuth } from '../store/AuthContext';
import styles from './EventPage.module.css';

import AudienceQA from './activities/AudienceQA';
import MultipleChoice from './activities/MultipleChoice';
import WordCloud from './activities/WordCloud';
import OpenText from './activities/OpenText';
import Ranking from './activities/Ranking';
import Rating from './activities/Rating';
import Quiz from './activities/Quiz';
import SpinnerWheel from './activities/SpinnerWheel';
import CorrectOrder from './activities/CorrectOrder';
import Categories from './activities/Categories';
import RatingScale from './activities/RatingScale';
import ImageChoice from './activities/ImageChoice';
import Slider from './activities/Slider';
import NumericInput from './activities/NumericInput';
import ClickImage from './activities/ClickImage';
import BudgetAllocation from './activities/BudgetAllocation';
import Reaction from './activities/Reaction';
import PairMatching from './activities/PairMatching';
import Prediction from './activities/Prediction';
import TimelineOrder from './activities/TimelineOrder';
import Hotspot from './activities/Hotspot';
import Debate from './activities/Debate';
import GuessNumber from './activities/GuessNumber';
import SpeedQuiz from './activities/SpeedQuiz';
import Drawing from './activities/Drawing';
import ScratchCard from './activities/ScratchCard';
import SlotMachine from './activities/SlotMachine';

const ACTIVITY_META = {
    'audience-qa': { label: 'Audience Q&A', icon: <MessageCircle size={18} />, color: 'var(--cat-blue)' },
    'multiple-choice': { label: 'Multiple Choice', icon: <CheckSquare size={18} />, color: 'var(--cat-violet)' },
    'word-cloud': { label: 'Word Cloud', icon: <Cloud size={18} />, color: 'var(--cat-cyan)' },
    'open-text': { label: 'Open Text', icon: <AlignLeft size={18} />, color: 'var(--cat-green)' },
    'ranking': { label: 'Ranking', icon: <ListOrdered size={18} />, color: 'var(--cat-orange)' },
    'rating': { label: 'Rating', icon: <Star size={18} />, color: 'var(--cat-amber)' },
    'quiz': { label: 'Quiz', icon: <Brain size={18} />, color: 'var(--cat-rose)' },
    'spinner-wheel': { label: 'Spinner Wheel', icon: <Disc3 size={18} />, color: 'var(--cat-pink)' },
    'correct-order': { label: 'Correct Order', icon: <ArrowUpDown size={18} />, color: 'var(--cat-teal)' },
    'categories': { label: 'Categories', icon: <LayoutGrid size={18} />, color: 'var(--cat-violet)' },
    'rating-scale': { label: 'Rating Scale', icon: <SlidersHorizontal size={18} />, color: 'var(--cat-teal)' },
    'image-choice': { label: 'Image Choice', icon: <ImageIcon size={18} />, color: 'var(--cat-indigo)' },
    'slider': { label: 'Slider', icon: <SlidersHorizontal size={18} />, color: 'var(--cat-teal)' },
    'numeric-input': { label: 'Numeric Input', icon: <Hash size={18} />, color: 'var(--cat-violet)' },
    'click-image': { label: 'Click on Image', icon: <MousePointerClick size={18} />, color: 'var(--cat-cyan)' },
    'budget-allocation': { label: 'Budget Allocation', icon: <CircleDollarSign size={18} />, color: 'var(--cat-amber)' },
    'reaction': { label: 'Reaction', icon: <Smile size={18} />, color: 'var(--cat-pink)' },
    'pair-matching': { label: 'Pair Matching', icon: <Shuffle size={18} />, color: 'var(--cat-violet)' },
    'prediction': { label: 'Prediction', icon: <TrendingUp size={18} />, color: 'var(--cat-orange)' },
    'timeline-order': { label: 'Timeline Ordering', icon: <Clock size={18} />, color: 'var(--cat-teal)' },
    'hotspot': { label: 'Hotspot', icon: <Target size={18} />, color: 'var(--cat-rose)' },
    'debate': { label: 'Debate', icon: <MessageSquare size={18} />, color: 'var(--cat-blue)' },
    'guess-number': { label: 'Guess the Number', icon: <Hash size={18} />, color: 'var(--cat-orange)' },
    'speed-quiz': { label: 'Speed Quiz', icon: <Zap size={18} />, color: 'var(--cat-rose)' },
    'drawing': { label: 'Drawing', icon: <Pencil size={18} />, color: 'var(--cat-violet)' },
    'scratch-card': { label: 'Scratch Card', icon: <Ticket size={18} />, color: 'var(--cat-amber)' },
    'slot-machine': { label: 'Slot Machine', icon: <Cherry size={18} />, color: 'var(--cat-orange)' },
};

const COMPONENTS = {
    'audience-qa': AudienceQA,
    'multiple-choice': MultipleChoice,
    'word-cloud': WordCloud,
    'open-text': OpenText,
    'ranking': Ranking,
    'rating': Rating,
    'quiz': Quiz,
    'spinner-wheel': SpinnerWheel,
    'correct-order': CorrectOrder,
    'categories': Categories,
    'rating-scale': RatingScale,
    'image-choice': ImageChoice,
    'slider': Slider,
    'numeric-input': NumericInput,
    'click-image': ClickImage,
    'budget-allocation': BudgetAllocation,
    'reaction': Reaction,
    'pair-matching': PairMatching,
    'prediction': Prediction,
    'timeline-order': TimelineOrder,
    'hotspot': Hotspot,
    'debate': Debate,
    'guess-number': GuessNumber,
    'speed-quiz': SpeedQuiz,
    'drawing': Drawing,
    'scratch-card': ScratchCard,
    'slot-machine': SlotMachine,
};

export default function EventPage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const refreshUserRef = React.useRef(refreshUser);
    refreshUserRef.current = refreshUser;
    const [showPicker, setShowPicker] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isQrOpen, setIsQrOpen] = useState(false);

    const {
        event,
        activities,
        activeActivityId,
        setActiveActivityId,
        activeActivity,
        isFetching,
        fetchError,
        addActivityError,
        setAddActivityError,
        deletingId,
        openDropdownId,
        setOpenDropdownId,
        isLaunchingId,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        launchedActivityId,
        shareNote,
        joinUrl,
        handleActivitySelected,
        handleDeleteActivity,
        handleDuplicateActivity,
        handleLaunchActivity,
        handleStopActivity,
        handleUpdateActivitySetting,
        handleResetResults,
        handleCopyShare,
        handleCopyDirectLink,
        handleActivitySettingsChange,
        handleKeySelect,
        handleReorderActivities,
    } = useEventPage(eventId, { onPollCreated: () => refreshUserRef.current?.() });

    const ActiveComponent = useMemo(
        () => (activeActivity ? COMPONENTS[activeActivity.type] : null),
        [activeActivity]
    );
    const eventExpired = useMemo(() => {
        if (!event?.end_date) return false;
        const end = new Date(event.end_date);
        end.setHours(23, 59, 59, 999);
        return new Date() > end;
    }, [event?.end_date]);
    const eventExpiredStartMessage = 'Event duration has expired. Extend event end date to start polls again.';

    useEffect(() => {
        if (addActivityError) refreshUser();
    }, [addActivityError, refreshUser]);

    const onActivitySelected = (activityType) => {
        handleActivitySelected(activityType);
        setShowPicker(false);
        setIsSidebarOpen(false);
    };

    // Check if an activity has a valid question (required for launching)
    const activityHasValidQuestion = (act) => {
        if (!act) return false;
        if (act.type === 'audience-qa') return true; // audience-qa has no question field
        if (act.type === 'quiz') return !!act.content?.questions?.[0]?.text?.trim();
        return !!act.content?.question?.trim();
    };

    const handleNextActivity = async () => {
        const idx = activities.findIndex((a) => a.id === activeActivityId);
        // Find the next activity that has a valid question
        let nextIdx = idx + 1;
        while (nextIdx < activities.length && !activityHasValidQuestion(activities[nextIdx])) {
            nextIdx++;
        }
        if (nextIdx < activities.length) {
            const next = activities[nextIdx];
            if (launchedActivityId) {
                // Launching directly overwrites the event's active_activity_id server-side —
                // no need to stop first. The previous stop-then-launch sequence meant two
                // serialized round-trips per click, plus a broadcast "activity:stopped" that
                // flashed participants to a blank waiting screen before the new poll arrived
                // moments later.
                await handleLaunchActivity(next.id);
            } else {
                setActiveActivityId(next.id);
            }
        }
    };

    const handlePrevActivity = async () => {
        const idx = activities.findIndex((a) => a.id === activeActivityId);
        // Find the previous activity that has a valid question
        let prevIdx = idx - 1;
        while (prevIdx >= 0 && !activityHasValidQuestion(activities[prevIdx])) {
            prevIdx--;
        }
        if (prevIdx >= 0) {
            const prev = activities[prevIdx];
            if (launchedActivityId) {
                await handleLaunchActivity(prev.id);
            } else {
                setActiveActivityId(prev.id);
            }
        }
    };

    if (isFetching) {
        return (
            <div className={styles.loadingState}>
                <Loader2 size={28} className={styles.spinner} />
                <span>Loading...</span>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className={styles.loadingState}>
                <p className={styles.emptyPaneTitle}>
                    {fetchError.toLowerCase().includes('expired')
                        ? 'Your admin access has expired. You cannot access this event. Contact superadmin to renew.'
                        : fetchError}
                </p>
                <button type="button" className={styles.backBtn} onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                </button>
            </div>
        );
    }

    if (!event) {
        return (
            <div className={styles.loadingState}>
                <p className={styles.emptyPaneTitle}>This event could not be found or you don’t have access to it.</p>
                <button type="button" className={styles.backBtn} onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {addActivityError && (
                <div className={styles.creditsErrorBanner} role="alert">
                    <AlertCircle size={16} aria-hidden />
                    <span>{addActivityError}</span>
                    <Link to="/credits" className={styles.creditsErrorLink}>Redeem coupon in Credits & Coupon</Link>
                    <button type="button" className={styles.creditsErrorDismiss} onClick={() => setAddActivityError('')} aria-label="Dismiss">
                        <X size={14} />
                    </button>
                </div>
            )}
            {isQrOpen && joinUrl && (
                <QrModal eventName={event.name} eventCode={event.code} joinUrl={joinUrl} onClose={() => setIsQrOpen(false)} />
            )}

            <PollsSidebar
                activities={activities}
                activeActivityId={activeActivityId}
                launchedActivityId={launchedActivityId}
                isSidebarOpen={isSidebarOpen}
                isSidebarCollapsed={isSidebarCollapsed}
                openDropdownId={openDropdownId}
                deletingId={deletingId}
                isLaunchingId={isLaunchingId}
                activityMeta={ACTIVITY_META}
                onAddClick={() => setShowPicker(true)}
                onCollapseToggle={() => setIsSidebarCollapsed((v) => !v)}
                onSelectActivity={setActiveActivityId}
                onKeyDown={handleKeySelect}
                onToggleDropdown={(id) => setOpenDropdownId((prev) => (id == null ? null : prev === id ? null : id))}
                onCopyDirectLink={handleCopyDirectLink}
                onResetResults={handleResetResults}
                onDuplicate={handleDuplicateActivity}
                onDelete={handleDeleteActivity}
                onToggleResults={(activity) =>
                    handleUpdateActivitySetting(activity.id, 'pollResults', activity.content?.settings?.pollResults === 'hidden' ? 'visible' : 'hidden')
                }
                onToggleCorrectAnswer={(activity) =>
                    handleUpdateActivitySetting(activity.id, 'showCorrectAnswer', !activity.content?.settings?.showCorrectAnswer)
                }
                onToggleLock={(activity) =>
                    handleUpdateActivitySetting(activity.id, 'isLocked', !activity.content?.settings?.isLocked)
                }
                onLaunch={handleLaunchActivity}
                onStop={handleStopActivity}
                onBackdropClick={() => setIsSidebarOpen(false)}
                onReorderActivities={handleReorderActivities}
                eventExpired={eventExpired}
                eventExpiredMessage={eventExpiredStartMessage}
            />

            <div className={styles.contentPane}>
                <div className={styles.mobileTopbar}>
                    <button type="button" className={styles.addBtn} onClick={() => setIsSidebarOpen(true)} aria-label="Open interactions">
                        <ListOrdered size={16} /> Interactions
                    </button>
                    <div className={styles.mobileTopbarSpacer} />
                    <button type="button" className={styles.addBtn} onClick={() => setShowPicker(true)}>
                        <Plus size={16} /> Add
                    </button>
                </div>

                <EventHeader
                    event={event}
                    joinUrl={joinUrl}
                    shareNote={shareNote}
                    activities={activities}
                    launchedActivityId={launchedActivityId}
                    user={user}
                    onCopyCode={(code) => handleCopyShare(code, 'Event code')}
                    onCopyLink={(url) => handleCopyShare(url, 'Join link')}
                    onShowQr={() => setIsQrOpen(true)}
                />

                {fetchError && (
                    <div className={styles.errorBanner} role="alert">
                        <AlertCircle size={16} aria-hidden />
                        <span>{fetchError}</span>
                    </div>
                )}

                {!activeActivityId || !ActiveComponent ? (
                    <div className={styles.emptyPane}>
                        <div className={styles.emptyPaneIcon}><Sparkles size={26} aria-hidden /></div>
                        <div className={styles.emptyPaneTitle}>Select an interaction to edit, or create a new one to get started.</div>
                        <p className={styles.emptyPaneHint}>
                            Tip: start with Multiple Choice for quick engagement, then add Word Cloud or Q&A for deeper feedback.
                        </p>
                        <button type="button" className={styles.addBtn} style={{ padding: 'var(--space-3) var(--space-6)' }} onClick={() => setShowPicker(true)}>
                            <Plus size={18} /> New interaction
                        </button>
                    </div>
                ) : (
                    <ActiveComponent
                        key={activeActivity.id}
                        event={event}
                        activity={activeActivity}
                        isLaunched={launchedActivityId === activeActivity.id}
                        onLaunch={handleLaunchActivity}
                        onStop={handleStopActivity}
                        onNext={handleNextActivity}
                        onPrev={handlePrevActivity}
                        hasNext={(() => {
                            const idx = activities.findIndex((a) => a.id === activeActivity.id);
                            return activities.slice(idx + 1).some(activityHasValidQuestion);
                        })()}
                        hasPrev={(() => {
                            const idx = activities.findIndex((a) => a.id === activeActivity.id);
                            return activities.slice(0, idx).some(activityHasValidQuestion);
                        })()}
                        onSettingsUpdated={handleActivitySettingsChange}
                        launchDisabledReason={eventExpired ? eventExpiredStartMessage : ''}
                    />
                )}
            </div>

            <ActivityTypeModal isOpen={showPicker} event={event} onClose={() => setShowPicker(false)} onSelect={onActivitySelected} />
        </div>
    );
}
